import {
  Timestamp,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { QuickTalesRef } from "../../../../firebase";
import { getSingleUser } from "../../../../modules/OrganizationQuickTales/hooks/QuickTalesHooks";
import moment from "moment";
import { getMostRecentUserContentReport } from "../../GAContentModeration/hooks/ContentReportsHooks";

export const getAllGAQuickTales = async () => {
  try {
    // Order newest-first on the server so the rows shown on the first page come
    // back first and the client-side stableSort has far less work to do.
    //
    // NOTE: Firestore's orderBy() silently EXCLUDES documents that are missing
    // the ordered field. All QuickTales are created with `createdAt`, but to be
    // safe against any legacy docs lacking it, we fall back to an unordered
    // fetch if the ordered query unexpectedly returns nothing.
    const orderedQuery = query(QuickTalesRef, orderBy("createdAt", "desc"));
    const response = await getDocs(orderedQuery);

    if (response.empty) {
      const fallback = await getDocs(QuickTalesRef);
      return fallback;
    }

    return response;
  } catch (error) {
    // If the ordered query fails for any reason (e.g. missing index), fall back
    // to the plain collection fetch so the page still loads.
    console.log("Error fetching ordered QuickTales, falling back:", error.message);
    try {
      return await getDocs(QuickTalesRef);
    } catch (fallbackError) {
      console.log(
        "Error fetching data from Firestore:",
        fallbackError.message
      );
      return [];
    }
  }
};



export const getFilterQuickTales = async (
  whoCanListenFilterOptions,
  statusFilterOptions,
  reportedStatusFilterOptions,
  creationDateRange,
  setLoading,
  handleFilterModalClose
) => {
  setLoading(true);

  const deduplicate = (arr) => {
    const uniqueIds = new Set();
    const uniqueDocs = [];
    arr.forEach((doc) => {
      if (!uniqueIds.has(doc.id)) {
        uniqueIds.add(doc.id);
        uniqueDocs.push(doc);
      }
    });
    // console.log("results:", uniqueDocs);
    handleFilterModalClose();
    return uniqueDocs;
  };

  try {
    let results = [];

    const statusOptions =
      statusFilterOptions.length > 0 ? statusFilterOptions : [null];
    const whoCanListenOptions =
      whoCanListenFilterOptions.length > 0 ? whoCanListenFilterOptions : [null];
    const reportedStatusOptions =
      reportedStatusFilterOptions.length > 0
        ? reportedStatusFilterOptions
        : [null];

    for (const status of statusOptions) {
      for (const whoCanListen of whoCanListenOptions) {
        for (const reportedStatus of reportedStatusOptions) {
          let queries = [];

          if (status !== null) {
            queries.push(
              where("isDraft", "==", status === "Draft" ? true : false)
            );
          }

          if (whoCanListen !== null) {
            queries.push(where("status", "==", whoCanListen));
          }

          if (reportedStatus !== null) {
            queries.push(where("reportedStatus", "==", reportedStatus));
          }

          if (
            creationDateRange &&
            creationDateRange.startDate &&
            creationDateRange.endDate
          ) {
            queries.push(where("createdAt", ">=", creationDateRange.startDate));
            queries.push(where("createdAt", "<=", creationDateRange.endDate));
          }

          if (queries.length > 0) {
            const q = query(QuickTalesRef, ...queries);
            const querySnapshot = await getDocs(q);

            const fetchDetailsPromises = querySnapshot.docs.map(async (doc) => {
              let docData = doc.data();
              const userTemp = await getSingleUser(docData.createdBy);
              const narratorTemp = await getSingleUser(docData.narrator);
              let reactionReportsTemp = await getMostRecentUserContentReport(
                doc.id
              );
              // console.log(reactionReportsTemp);
              let statusTemp = "";

              // Extract the status from the report, if it exists
              reactionReportsTemp.forEach((doc) => {
                // console.log("report::", doc);
                statusTemp = doc?.status?.status;
              });
              return {
                id: doc.id,
                ...docData,
                photoUrl: docData?.imageUrls?.[0] ?? docData?.photoUrl ?? "",
                createdByData: userTemp?.data() ? userTemp?.data() : null,
                narratorData: narratorTemp?.data()
                  ? narratorTemp?.data()
                  : null,
                reportedStatus: statusTemp,
              };
            });

            const queryResults = await Promise.all(fetchDetailsPromises);
            results = results.concat(queryResults);
          }
        }
      }
    }

    const uniqueResults = deduplicate(results);
    setLoading(false);
    return uniqueResults;
  } catch (error) {
    console.error("Error filtering QuickTales:", error);
    setLoading(false);
    return [];
  }
};

// export const getFilterQuickTales = async (
//   whoCanListenFilterOptions,
//   statusFilterOptions,
//   reportedStatusFilterOptions,
//   creationDateFilterOptions,
//   creationDateRange,
//   setLoading,
//   handleFilterModalClose
// ) => {
//   // Set loading to true at the beginning of the function
//   // setLoading(true);
//   const deduplicate = (arr) => {
//     const uniqueIds = new Set();
//     const uniqueDocs = [];
//     arr.forEach((doc) => {
//       if (!uniqueIds.has(doc.id)) {
//         uniqueIds.add(doc.id);
//         uniqueDocs.push(doc);
//       }
//     });
//     console.log("results:", uniqueDocs);
//     handleFilterModalClose();
//     return uniqueDocs;
//   };

//   try {
//     // Define the reference to your QuickTales collection
//     // let results = [];

//     let queries = [
//       ...statusFilterOptions.map(
//         (option) => where("isDraft", "==", option === "Draft" ? true : false)
//         // where("approvalStatus.status", "==", option)
//       ),
//       ...whoCanListenFilterOptions.map((option) =>
//         where("status", "==", option)
//       ),
//       ...reportedStatusFilterOptions.map((option) =>
//         where("reportedStatus", "==", option)
//       ),
//     ];

//     // Add date range filter if provided
//     if (creationDateFilterOptions.length > 0) {
//       let selectedDateOption;

//       if (Array.isArray(creationDateFilterOptions)) {
//         // Example of finding a specific option, adjust logic as necessary
//         const dateRangeOption = creationDateFilterOptions.find((option) =>
//           option.startsWith("Creation Date:")
//         );
//         if (dateRangeOption) {
//           selectedDateOption = "Custom Range"; // Indicate a custom range is selected
//         } else {
//           // Logic to set selectedDateOption based on other criteria
//           selectedDateOption = creationDateFilterOptions[0]; // Example defaulting to the first option
//         }
//       } else {
//         selectedDateOption = creationDateFilterOptions; // Directly use the string if it's not an array
//       }
//       switch (selectedDateOption) {
//         case "Custom Range":
//           const dateRangeOption = creationDateFilterOptions.find((option) =>
//             option.startsWith("Creation Date:")
//           );
//           const dates = dateRangeOption
//             .replace("Creation Date: ", "")
//             .split(" - ");
//           const startDate = moment(dates[0], "MM/DD/YYYY")
//             .startOf("day")
//             .toDate();
//           const endDate = moment(dates[1], "MM/DD/YYYY").endOf("day").toDate();

//           // Add date range conditions to the query
//           queries.push(where("createdAt", ">=", Timestamp.fromDate(startDate)));
//           queries.push(where("createdAt", "<=", Timestamp.fromDate(endDate)));
//           break;
//         case "Today":
//           const todayStart = moment(new Date(), "MM/DD/YYYY")
//             .startOf("day")
//             .toDate();
//           const todayEnd = moment(new Date(), "MM/DD/YYYY")
//             .endOf("day")
//             .toDate();
//           queries.push(
//             where("createdAt", ">=", Timestamp.fromDate(todayStart))
//           );
//           queries.push(where("createdAt", "<=", Timestamp.fromDate(todayEnd)));
//           break;
//         case "Past Week":
//           const lastWeekStart = moment(new Date(), "MM/DD/YYYY")
//             .subtract(7, "days")
//             .startOf("day")
//             .toDate();
//           const lastWeekEnd = moment(new Date(), "MM/DD/YYYY")
//             .endOf("day")
//             .toDate();
//           queries.push(
//             where("createdAt", ">=", Timestamp.fromDate(lastWeekStart))
//           );
//           queries.push(
//             where("createdAt", "<=", Timestamp.fromDate(lastWeekEnd))
//           );
//           break;
//         case "Last 30 Days":
//           const last30DaysStart = moment(new Date(), "MM/DD/YYYY")
//             .subtract(30, "days")
//             .startOf("day")
//             .toDate();
//           const last30DaysEnd = moment(new Date(), "MM/DD/YYYY")
//             .endOf("day")
//             .toDate();
//           queries.push(
//             where("createdAt", ">=", Timestamp.fromDate(last30DaysStart))
//           );
//           queries.push(
//             where("createdAt", "<=", Timestamp.fromDate(last30DaysEnd))
//           );
//           break;
//         case "Last 12 Months":
//           const last12MonthsStart = moment(new Date(), "MM/DD/YYYY")
//             .subtract(12, "months")
//             .startOf("day")
//             .toDate();
//           const last12MonthsEnd = moment(new Date(), "MM/DD/YYYY")
//             .endOf("day")
//             .toDate();
//           queries.push(
//             where("createdAt", ">=", Timestamp.fromDate(last12MonthsStart))
//           );
//           queries.push(
//             where("createdAt", "<=", Timestamp.fromDate(last12MonthsEnd))
//           );
//           break;
//         default:
//           // Default case
//           break;
//       }
//     }
//     // Execute the query
//     const q = query(QuickTalesRef, ...queries);
//     const querySnapshot = await getDocs(q);

//     // Convert to map to handle async operations
//     const fetchDetailsPromises = querySnapshot.docs.map(async (doc) => {
//       const userTemp = await getSingleUser(doc.data().createdBy);
//       const narratorTemp = await getSingleUser(doc.data().narrator);
//       return {
//         id: doc.id,
//         ...doc.data(),
//         createdByData: userTemp?.data() ? userTemp?.data() : {},
//         narratorData: narratorTemp?.data() ? narratorTemp?.data() : {},
//       };
//     });

//     // Wait for all fetches to complete
//     const results = await Promise.all(fetchDetailsPromises);

//     const uniqueResults = deduplicate(results);
//     handleFilterModalClose(); // Assuming it's appropriate to close the modal here
//     return uniqueResults;
//     // return deduplicatedResults;
//   } catch (error) {
//     console.error("Error filtering QuickTales:", error);
//     setLoading(false);
//     return []; // Return an empty array in case of error
//   }
// };
